import { useEffect, useState } from "react";
import { Table, useTable } from "ka-table";
import {
  DataType,
  EditingMode,
  PagingPosition,
  SortingMode,
  FilteringMode,
} from "ka-table/enums";
import { MdArrowOutward, MdDeleteForever } from "react-icons/md";
import Swal from "sweetalert2";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Link } from "react-router-dom";

const CatalogueTable = () => {
  const [catalogues, setCatalogues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const tableInstance = useTable();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    fetch("https://backend-server-nine-opal.vercel.app/catalogues")
      .then((res) => res.json())
      .then((data) => {
        setCatalogues(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading catalogues:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This catalogue will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`https://backend-server-nine-opal.vercel.app/catalogues/${id}`, {
          method: "DELETE",
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.deletedCount > 0) {
              Swal.fire("Deleted!", "Catalogue has been removed.", "success");
              setCatalogues((prev) => prev.filter((c) => c._id !== id));
            }
          });
      }
    });
  };

  if (loading) {
    return <p className="text-center">Loading catalogues...</p>;
  }

  if (catalogues.length === 0) {
    return <p className="text-gray-600">No catalogues uploaded yet.</p>;
  }

  const data = catalogues.map((cat, index) => ({
    id: index + 1,
    _id: cat._id,
    name: cat.catalogueName,
    brand: cat.brand,
    description: cat.description,
    pdfDownloadLink: cat.pdfDownloadLink,
  }));

  const columns = [
    { key: "id", title: "No.", dataType: DataType.Number, isFilterable: false },
    {
      key: "name",
      title: "Catalogue Name",
      dataType: DataType.String,
      isFilterable: false,
    },
    { key: "brand", title: "Brand", dataType: DataType.String },
    {
      key: "description",
      title: "Description",
      dataType: DataType.String,
      isFilterable: false,
    },
    {
      key: "pdfDownloadLink",
      title: "File",
      dataType: DataType.String,
      isFilterable: false,
    },
    {
      key: "actions",
      title: "Actions",
      isEditable: false,
      isFilterable: false,
    },
  ];

  return (
    <div className="mt-10">
      <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center mb-4">
        <h2 className="text-2xl font-bold mb-4 text-left">
          Uploaded Catalogues: <span className="text-[#036c0f]">{catalogues.length}</span>
        </h2>
        <Link
          to="/dashboard/catalogueUpload"
          className="order-1 sm:order-3 lg:order-3 w-full sm:w-auto"
        >
          <button className="w-full sm:w-auto whitespace-nowrap flex justify-center items-center px-4 py-2 rounded-lg font-semibold text-sm lg:text-base border-none text-white bg-[#080808] hover:bg-[#f0ca4e] hover:text-black">
            Upload new Catalogue <MdArrowOutward className="ml-1 text-lg" />
          </button>
        </Link>
      </div>

      {isMobile ? (
        /* ✅ Mobile Cards */
        <div className="grid grid-cols-1 gap-4 text-left">
          {catalogues.map((cat) => (
            <div
              key={cat._id}
              className="border rounded-lg p-4 shadow-sm bg-white"
            >
              <h2 className="font-bold text-lg">{cat.catalogueName}</h2>
              <p className="text-sm text-gray-600">Brand: {cat.brand}</p>
              <p className="text-sm text-gray-600 line-clamp-3">
                {cat.description}
              </p>
              <a
                href={cat.pdfDownloadLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline text-sm block mt-2"
              >
                View PDF
              </a>
              <button
                onClick={() => handleDelete(cat._id)}
                className="btn btn-sm bg-red-500 text-white hover:bg-red-600 mt-2"
              >
                {" "}
                Delete
                <MdDeleteForever />
              </button>
            </div>
          ))}
        </div>
      ) : (
        /* ✅ Desktop Table */
        <div className="min-w-[800px] overflow-x-auto">
          <Table
            table={tableInstance}
            className="w-full text-sm"
            columns={columns}
            data={data}
            rowKeyField="_id"
            paging={{
              enabled: true,
              pageIndex: 0,
              pageSize: 5,
              pageSizes: [5, 10, 15],
              position: PagingPosition.Bottom,
            }}
            sortingMode={SortingMode.Single}
            editingMode={EditingMode.None}
            filteringMode={FilteringMode.HeaderFilter}
            childComponents={{
              cellText: {
                content: ({ column, rowData }) => {
                  if (column.key === "pdfDownloadLink") {
                    return (
                      <a
                        href={rowData.pdfDownloadLink}
                        download
                        className="text-green-600 font-semibold underline"
                      >
                        Download PDF
                      </a>
                    );
                  }
                  if (column.key === "description") {
                    const words = rowData.description?.split(" ") || [];
                    const isExpanded = expandedRows[rowData._id];
                    const displayText = isExpanded
                      ? rowData.description
                      : words.slice(0, 10).join(" ") +
                        (words.length > 10 ? "..." : "");

                    return (
                      <span>
                        {displayText}{" "}
                        {words.length > 30 && (
                          <button
                            onClick={() => toggleExpand(rowData._id)}
                            className="text-blue-500 underline ml-1"
                          >
                            {isExpanded ? "Show Less" : "View Full"}
                          </button>
                        )}
                      </span>
                    );
                  }
                  if (column.key === "actions") {
                    return (
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleDelete(rowData._id)}
                          className="btn btn-xs bg-red-500 text-white hover:bg-red-600"
                        >
                          Delete
                          <MdDeleteForever />
                        </button>
                      </div>
                    );
                  }
                },
              },
              pagingPages: {
                content: ({ pagesCount, dispatch, pageIndex }) => {
                  const pages = [];
                  for (let i = 0; i < pagesCount; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() =>
                          dispatch({ type: "PageIndexChanged", pageIndex: i })
                        }
                        className={`px-3 py-1 mx-1 rounded ${
                          i === pageIndex
                            ? "bg-black text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  return (
                    <div className="flex items-center justify-center mt-4 space-x-2">
                      {/* Previous */}
                      <button
                        onClick={() =>
                          pageIndex > 0 &&
                          dispatch({
                            type: "PageIndexChanged",
                            pageIndex: pageIndex - 1,
                          })
                        }
                        disabled={pageIndex === 0}
                        className="px-3 flex items-center  py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                      >
                        <IoIosArrowBack />
                        Previous
                      </button>

                      {pages}

                      {/* Next */}
                      <button
                        onClick={() =>
                          pageIndex < pagesCount - 1 &&
                          dispatch({
                            type: "PageIndexChanged",
                            pageIndex: pageIndex + 1,
                          })
                        }
                        disabled={pageIndex >= pagesCount - 1}
                        className="px-3 flex items-center  py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                      >
                        Next
                        <IoIosArrowForward />
                      </button>
                    </div>
                  );
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CatalogueTable;
